/**
 * Classe para manipulação de cores
 *
 * @author Wellington Pereira
 * @since 2020-03-28
 * @version 1.0.0
 */


class Color {

  // Normaliza a cor para o intervalo [0, 1]
  #normalizeColor(color) {
    return color <= 0.03928 ? color / 12.92 : Math.pow((color + 0.055) / 1.055, 2.4);
  }

  // Calcula a luminância
  #calculateLuminance(r, g, b) {
    const rNormalized = r / 255;
    const gNormalized = g / 255;
    const bNormalized = b / 255;
    return 0.2126 * this.#normalizeColor(rNormalized) +
      0.7152 * this.#normalizeColor(gNormalized) +
      0.0722 * this.#normalizeColor(bNormalized);
  }

  // Calcula a razão de contraste
  #calculateContrast(luminance1, luminance2) {
    const L1 = Math.max(luminance1, luminance2) + 0.05;
    const L2 = Math.min(luminance1, luminance2) + 0.05;
    return L1 / L2;
  }

  autoForeColor(inputColor) {
    let r, g, b;
    // Converte a cor para RGB
    if (inputColor.startsWith('#')) {
      [r, g, b] = this.hex2rgb(inputColor);
    } else if (inputColor.startsWith('rgb')) {
      const rgbValues = inputColor.match(/\d+/g);
      r = parseInt(rgbValues[0]);
      g = parseInt(rgbValues[1]);
      b = parseInt(rgbValues[2]);
    } else {
      [r, g, b] = this.color2rgb(inputColor);
    }

    // Normaliza os valores RGB para o intervalo [0, 1]
    const rNormalized = r / 255;
    const gNormalized = g / 255;
    const bNormalized = b / 255;

    // Calcula a luminância relativa
    const luminance = 0.2126 * this.#normalizeColor(rNormalized) +
      0.7152 * this.#normalizeColor(gNormalized) +
      0.0722 * this.#normalizeColor(bNormalized);

    // Define a razão de contraste mínima aceitável (WCAG AA)
    const contrastThreshold = 3.5;

    // Calcula a luminância para preto e branco
    const luminanceBlack = this.#calculateLuminance(0, 0, 0);
    const luminanceWhite = this.#calculateLuminance(255, 255, 255);

    // Calcula a razão de contraste entre a cor de fundo e o texto preto/branco
    const contrastBlack = this.#calculateContrast(luminance, luminanceBlack);
    const contrastWhite = this.#calculateContrast(luminance, luminanceWhite);

    // Retorna preto ou branco baseado na razão de contraste
    return contrastWhite <= contrastThreshold ? '#000000' : '#FFFFFF';
  }

  color2rgb(name) {
    const colors = {
      black: [0, 0, 0],
      blue: [0, 0, 255],
      cyan: [0, 255, 255],
      gray: [128, 128, 128],
      green: [0, 128, 0],
      lime: [0, 255, 0],
      magenta: [255, 0, 255],
      maroon: [128, 0, 0],
      navy: [0, 0, 128],
      olive: [128, 128, 0],
      orange: [255, 165, 0],
      purple: [128, 0, 128],
      red: [255, 0, 0],
      silver: [192, 192, 192],
      teal: [0, 128, 128],
      white: [255, 255, 255],
      yellow: [255, 255, 0],
      yellowgreen: [154, 205, 50]
      // Adicione mais cores conforme necessário
    };
    return colors[name.toLowerCase()] || [0, 0, 0];
  }

  getColorForValue(value, scale, prefix = '') {
    // converte valor para número, tratando vírgulas e falsos
    const toNum = v => {
      if (v === false || v === null || v === undefined || v === '') return NaN;
      return Number(String(v).replace(',', '.'));
    };

    const numValue = toNum(value);

    const valid = Object.entries(scale)
      .map(([color, v]) => [color, toNum(v)])
      .filter(([, n]) => Number.isFinite(n))
      .sort((a, b) => a[1] - b[1]); // crescente

    if (!Number.isFinite(numValue) || valid.length === 0)
      return `${prefix}common`;

    // abaixo do menor valor → primeira cor
    if (numValue < valid[0][1]) return `${prefix}${valid[0][0]}`;
    // dentro das faixas
    for (let i = 0; i < valid.length - 1; i++) {
      const [color, min] = valid[i];
      const [, max] = valid[i + 1];
      if (numValue >= min && numValue < max) return `${prefix}${color}`;
    }

    // acima do último valor → última cor
    return `${prefix}${valid.at(-1)[0]}`;
  }


  /**
   * Converte HEX em RGB
   *
   * @param {string} hex
   * @returns {array}
   */
  hex2rgb(hex) {
    let r = parseInt(hex.substr(1, 2), 16);
    let g = parseInt(hex.substr(3, 2), 16);
    let b = parseInt(hex.substr(5, 2), 16);

    return [parseInt(r), parseInt(g), parseInt(b)];
  }

  /**
   * Converte HEX em HSL
   *
   * @param {string} hex
   * @returns {array}
   */
  hex2hsl(hex) {
    let r = parseInt(hex.substr(1, 2), 16);
    let g = parseInt(hex.substr(3, 2), 16);
    let b = parseInt(hex.substr(5, 2), 16);

    return this.rgb2hsl(r, g, b);
  }

  /**
   * Converte HEX em HSV
   *
   * @param {string} hex
   * @returns {array}
   */
  hex2hsv(hex) {
    let r = parseInt(hex.substr(1, 2), 16);
    let g = parseInt(hex.substr(3, 2), 16);
    let b = parseInt(hex.substr(5, 2), 16);

    return this.rgb2hsv(r, g, b);
  }

  /**
   * Converte HSL em HEX
   *
   * @param {number} h
   * @param {number} s
   * @param {number} l
   * @returns {string}
   */
  hsl2hex(h, s, l) {
    let rgb = this.hsl2rgb(h, s, l);
    return this.rgb2hex(rgb[0], rgb[1], rgb[2]);
  }

  /**
   * Converte HSL em HSV
   *
   * @param {number} h
   * @param {number} s
   * @param {number} l
   * @returns {array}
   */
  hsl2hsv(h, s, l) {
    let rgb = this.hsl2rgb(h, s, l);
    return this.rgb2hsv(rgb[0], rgb[1], rgb[2]);
  }

  /**
   * Converte HSL em RGB
   *
   * @param {number} h
   * @param {number} s
   * @param {number} l
   * @returns {array}
   */
  hsl2rgb(h, s, l) {
    h /= 360;
    s /= 100;
    l /= 100;

    let r, g, b;

    if (s == 0) {
      r = g = b = l;
    } else {
      let hue2rgb = (p, q, t) => {
        if (t < 0) {
          t += 1;
        }

        if (t > 1) {
          t -= 1;
        }

        if (t < 1 / 6) {
          return p + (q - p) * 6 * t;
        } else if (t < 1 / 2) {
          return q;
        } else if (t < 2 / 3) {
          return p + (q - p) * (2 / 3 - t) * 6;
        } else {
          return p;
        }
      };

      let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      let p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [parseInt(r * 255), parseInt(g * 255), parseInt(b * 255)];
  }

  /**
   * Converte HSV em HEX
   *
   * @param {number} h
   * @param {number} s
   * @param {number} v
   * @returns {string}
   */
  hsv2hex(h, s, v) {
    let rgb = this.hsv2rgb(h, s, v);
    return this.rgb2hex(rgb[0], rgb[1], rgb[2]);
  }

  /**
   * Converte HSV em HSL
   *
   * @param {number} h
   * @param {number} s
   * @param {number} v
   * @returns {array}
   */
  hsv2hsl(h, s, v) {
    let rgb = this.hsv2rgb(h, s, v);
    return this.rgb2hsl(rgb[0], rgb[1], rgb[2]);
  }

  /**
   * Converte HSV em RGB
   *
   * @param {number} h
   * @param {number} s
   * @param {number} l
   * @returns {array}
   */
  hsv2rgb(h, s, v) {
    h /= 360;
    s /= 100;
    v /= 100;

    let r, g, b;
    let i = Math.floor(h * 6);
    let f = h * 6 - i;
    let p = v * (1 - s);
    let q = v * (1 - f * s);
    let t = v * (1 - (1 - f) * s);

    switch (i % 6) {
      case 0: r = v; g = t; b = p; break;
      case 1: r = q; g = v; b = p; break;
      case 2: r = p; g = v; b = t; break;
      case 3: r = p; g = q; b = v; break;
      case 4: r = t; g = p; b = v; break;
      case 5: r = v; g = p; b = q; break;
    }

    return [parseInt(r * 255), parseInt(g * 255), parseInt(b * 255)];
  }

  /**
   * Converte RGB em HEX
   *
   * @param {number} r
   * @param {number} g
   * @param {number} b
   * @returns {string}
   */
  rgb2hex(r, g, b) {
    let tmpR = parseInt(r).toString(16);
    let tmpG = parseInt(g).toString(16);
    let tmpB = parseInt(b).toString(16);

    if (tmpR.length < 2) {
      tmpR = '0' + tmpR;
    }

    if (tmpG.length < 2) {
      tmpG = '0' + tmpG;
    }

    if (tmpB.length < 2) {
      tmpB = '0' + tmpB;
    }


    return tmpR + tmpG + tmpB;
  }

  /**
   * Converte RGB em HSL
   *
   * @param {number} r
   * @param {number} g
   * @param {number} b
   * @returns {array}
   */
  rgb2hsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;

    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let d, h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }

      h /= 6;
    }

    return [parseInt(h * 360), parseInt(s * 100), parseInt(l * 100)];
  }

  /**
   * Converte RGB em HSV
   *
   * @param {number} r
   * @param {number} g
   * @param {number} b
   * @returns {array}
   */
  rgb2hsv(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;

    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let h, s, v = max, d = max - min;

    s = max == 0 ? 0 : d / max;

    if (max == min) {
      h = 0;
    } else {
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return [parseInt(h * 360), parseInt(s * 100), parseInt(v * 100)];
  }
}

const color = new Color();
export const autoForeColor = (hex) => color.autoForeColor(hex);
export const getColorForValue = (value, scale, prefix) => color.getColorForValue(value, scale, prefix);
export const hex2rgb = (hex) => color.hex2rgb(hex);
export const hex2hsl = (hex) => color.hex2hsl(hex);
export const hex2hsv = (hex) => color.hex2hsv(hex);
export const hsl2hex = (h, s, l) => color.hsl2hex(h, s, l);
export const hsl2hsv = (h, s, l) => color.hsl2hsv(h, s, l);
export const hsl2rgb = (h, s, l) => color.hsl2rgb(h, s, l);
export const hsv2hex = (h, s, v) => color.hsv2hex(h, s, v);
export const hsv2hsl = (h, s, v) => color.hsv2hsl(h, s, v);
export const hsv2rgb = (h, s, v) => color.hsv2rgb(h, s, v);
export const rgb2hex = (r, g, b) => color.rgb2hex(r, g, b);
export const rgb2hsl = (r, g, b) => color.rgb2hsl(r, g, b);
export const rgb2hsv = (r, g, b) => color.rgb2hsv(r, g, b);

/** Parse explicit CSS colors without depending on browser layout or changing the stored value. */
export const parseColor = (value) => {
  const raw = String(value ?? '').trim();
  const hex = raw.match(/^#?([\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i);
  if (hex) {
    const expanded = hex[1].length <= 4
      ? [...hex[1]].map((digit) => digit.repeat(2)).join('')
      : hex[1];
    const [r, g, b, alpha = 255] = expanded.match(/../g).map((byte) => parseInt(byte, 16));
    return { r, g, b, a: alpha / 255 };
  }
  if (raw.toLowerCase() === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };

  const functional = raw.match(/^(rgba?|hsla?)\(([^)]+)\)$/i);
  if (!functional) return null;
  const parts = functional[2].trim().split(/[\s,/]+/);
  if (parts.length < 3 || parts.length > 4
    || parts.some((part) => !/^-?(?:\d+\.?\d*|\.\d+)%?$/.test(part)
      || !Number.isFinite(parseFloat(part)))) return null;
  const clamp = (number, max = 1) => Math.max(0, Math.min(max, number));
  const alpha = parts[3] ?? '1';
  const a = clamp(parseFloat(alpha) / (alpha.endsWith('%') ? 100 : 1));
  if (functional[1].toLowerCase().startsWith('rgb')) {
    const [r, g, b] = parts.slice(0, 3).map((part) => (
      Math.round(clamp(parseFloat(part) * (part.endsWith('%') ? 255 / 100 : 1), 255))
    ));
    return { r, g, b, a };
  }
  if (parts[0].endsWith('%') || !parts[1].endsWith('%') || !parts[2].endsWith('%')) return null;
  const hue = ((Number(parts[0]) % 360) + 360) % 360 / 30;
  const saturation = clamp(parseFloat(parts[1]) / 100);
  const lightness = clamp(parseFloat(parts[2]) / 100);
  const amplitude = saturation * Math.min(lightness, 1 - lightness);
  const channel = (offset) => {
    const position = (offset + hue) % 12;
    return Math.round(255 * (lightness - amplitude * Math.max(-1, Math.min(position - 3, 9 - position, 1))));
  };
  return { r: channel(0), g: channel(8), b: channel(4), a };
};

/** Format a color for display; persistence continues to use the original value. */
export const formatColor = (value, presentation = 'hex') => {
  const parsed = parseColor(value);
  if (!parsed) return String(value ?? '');
  const { r, g, b, a } = parsed;
  const round = (number, places = 3) => Number(number.toFixed(places));
  if (presentation === 'rgba') return `rgba(${r}, ${g}, ${b}, ${round(a)})`;
  if (presentation === 'hsla') {
    const channels = [r, g, b].map((channel) => channel / 255);
    const max = Math.max(...channels);
    const min = Math.min(...channels);
    const delta = max - min;
    const lightness = (max + min) / 2;
    const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
    let hue = 0;
    if (delta !== 0) {
      const [red, green, blue] = channels;
      hue = max === red ? (green - blue) / delta : max === green ? (blue - red) / delta + 2 : (red - green) / delta + 4;
      hue = (hue * 60 + 360) % 360;
    }
    return `hsla(${round(hue, 1)}, ${round(saturation * 100, 1)}%, ${round(lightness * 100, 1)}%, ${round(a)})`;
  }
  return `#${[r, g, b, ...(a < 1 ? [Math.round(a * 255)] : [])]
    .map((channel) => channel.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
};
